import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { cn } from "../../lib/utils.js";

/**
 * El botón es el objeto PULSABLE del sistema, y eso se dice con relieve, no
 * solo con color: cara con luz cenital (`sheen-*`), canto y sombra
 * (`shadow-control*`), y al pulsar se hunde — se apaga el degradado, la sombra
 * se vuelve hueco (`shadow-well`) y baja 1px. Es el par exacto del campo de
 * formulario, que está hundido siempre porque RECIBE en vez de sobresalir.
 *
 * `ghost` y `link` quedan fuera a propósito: no son objetos sino zonas de
 * texto sensibles, y darles cuerpo borraría la jerarquía con los otros cuatro.
 */
const PRESSED = "active:translate-y-px active:bg-none active:shadow-well";

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap",
    "rounded-md text-sm font-medium select-none",
    // La lista es explícita y no `transition-colors` porque hay que añadir
    // box-shadow y transform (el relieve y el hundido al pulsar). Va
    // `border-color` con ellas: `transition-colors` lo cubría, y hay consumers
    // que pasan `hover:border-*` por className.
    "transition-[color,background-color,border-color,box-shadow,transform] duration-150",
    "focus-visible:outline-none",
    "focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-0",
    "disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none",
  ].join(" "),
  {
    variants: {
      variant: {
        // primary/danger usan el paso `-strong`: el DEFAULT bajo texto blanco
        // da 3.98:1 y 3.76:1, por debajo del 4.5:1 de WCAG AA 1.4.3 para texto
        // normal (14px/500). El token de identidad sigue siendo el DEFAULT y se
        // usa donde el azul NO lleva blanco encima: foco, links, nav activa.
        primary: `bg-primary-strong sheen-control text-white shadow-control-primary hover:bg-primary-strong/90 ${PRESSED}`,
        secondary: `bg-surface-2 sheen-control text-text-primary shadow-control border border-border hover:bg-surface-3 ${PRESSED}`,
        ghost: "bg-transparent text-text-primary hover:bg-surface-2",
        danger: `bg-danger-strong sheen-control text-white shadow-control-danger hover:bg-danger-strong/90 ${PRESSED}`,
        outline: `bg-transparent text-text-primary border border-border shadow-control hover:bg-surface-2 hover:sheen-control ${PRESSED}`,
        link: "bg-transparent text-primary underline-offset-4 hover:underline p-0 h-auto",
      },
      size: {
        sm: "h-8 px-3 text-xs",
        md: "h-10 px-4 text-sm",
        lg: "h-12 px-6 text-base",
        icon: "h-10 w-10 p-0",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
